import axios, { type AxiosProgressEvent } from 'axios';
import type {
  MailListFilter,
  MailListFilterCounts,
  MailFolder,
  MailFolderType,
  MailMessage,
  MailMessageSummary,
  MailMessageListResponse,
  MailSearchResult,
  MailLabel,
  MailAccount,
  SendMessagePayload,
  SendIdentity,
} from '../types/mail.types';
import type { AppearancePreferences, NotificationPreferences } from '../store/settingsStore';
import type { AppPreferencesUpdate } from '@/types/appPreferences';
import { getAuthToken } from '@/api/axios';

// Dedicated Axios instance for EdgeMail client endpoints
const mailApi = axios.create({
  baseURL: '/api/edgemail',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Inject Bearer token from localStorage (set by Keycloak in App.tsx)
mailApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Include tenant realm header
  const firmId = localStorage.getItem('edgelexFirmId');
  if (firmId) {
    config.headers['X-Realm-ID'] = firmId;
  }
  return config;
});

// ── Mailbox Accounts ────────────────────────────────────────────────────────

// Map backend DB row shape to frontend MailAccount interface
function mapMailbox(raw: Record<string, unknown>): MailAccount {
  return {
    id: (raw.mailbox_id as string) || (raw.id as string),
    email: raw.email as string,
    displayName: (raw.display_name as string) || (raw.displayName as string) || '',
    avatarUrl: (raw.avatar_url as string) || undefined,
    isPrimary: !!(raw.is_primary ?? raw.isPrimary ?? false),
    tenantId: (raw.tenant_id as string) || (raw.tenantId as string) || '',
  };
}

export async function fetchMailboxAccounts(): Promise<MailAccount[]> {
  const { data } = await mailApi.get('/mailboxes');
  const rawMailboxes: Record<string, unknown>[] = data.mailboxes ?? data;
  return rawMailboxes.map(mapMailbox);
}

export async function fetchMailbox(mailboxId: string): Promise<MailAccount> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}`);
  return data;
}

export async function getMailboxQuota(mailboxId: string): Promise<{ usedBytes: number; quotaBytes: number }> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/quota`);
  // The server answers { quota: { used_bytes, quota_bytes, percent_used } }; older shapes are tolerated.
  const q = (data?.quota ?? data) as { used_bytes?: number; quota_bytes?: number; usedBytes?: number; quotaBytes?: number };
  return { usedBytes: Number(q.used_bytes ?? q.usedBytes ?? 0), quotaBytes: Number(q.quota_bytes ?? q.quotaBytes ?? 0) };
}

export async function requestMailboxSync(mailboxId: string): Promise<void> {
  await mailApi.post(`/mailboxes/${mailboxId}/sync`);
}

export async function fetchSendIdentities(mailboxId: string): Promise<SendIdentity[]> {
  const { data } = await mailApi.get<{ identities: SendIdentity[] }>(`/mailboxes/${mailboxId}/send-identities`);
  return data.identities;
}

export async function updateMailboxSettings(
  mailboxId: string,
  settings: Record<string, unknown>,
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/settings`, settings);
}

// ── User Preferences (Appearance/Notifications) ────────────────────────────

export interface UserPreferencesPayload {
  appearancePreferences?: Partial<AppearancePreferences>;
  notificationPreferences?: Partial<NotificationPreferences>;
  notificationDefaults?: Partial<NotificationPreferences>;
  notificationOverrides?: Partial<NotificationPreferences>;
  notificationPreferencesInherited?: boolean;
  appPreferences?: AppPreferencesUpdate;
}

export interface UserPreferencesUpdatePayload {
  appearancePreferences?: Partial<AppearancePreferences>;
  notificationPreferences?: Partial<NotificationPreferences> | null;
  appPreferences?: AppPreferencesUpdate;
}

export async function fetchUserPreferences(): Promise<UserPreferencesPayload | null> {
  try {
    const { data } = await mailApi.get('/users/me/preferences');
    return data ?? null;
  } catch {
    return null;
  }
}

export async function updateUserPreferences(prefs: UserPreferencesUpdatePayload): Promise<UserPreferencesPayload> {
  const { data } = await mailApi.put('/users/me/preferences', prefs);
  return data;
}

// ── Wallpapers (uploaded images; catalogue wallpapers are static assets) ────

export type DynamicWallpaperKind = 'custom' | 'firm';

/** Fetch an uploaded wallpaper as a blob (bearer auth; CSS url() cannot send it). */
export async function fetchWallpaperBlob(kind: DynamicWallpaperKind, opts: { thumb?: boolean } = {}): Promise<Blob | null> {
  const path = kind === 'custom' ? '/users/me/wallpaper' : '/wallpapers/firm';
  try {
    const { data } = await mailApi.get(path, { params: opts.thumb ? { thumb: '1' } : undefined, responseType: 'blob' });
    return data as Blob;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

/** Upload the user's own wallpaper. The server normalises it and returns the merged preferences. */
export async function uploadCustomWallpaper(file: Blob): Promise<UserPreferencesPayload> {
  const { data } = await mailApi.put('/users/me/wallpaper', file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    timeout: 120000,
  });
  return data;
}

/** Remove the user's wallpaper; the server falls back to the firm or product default and returns the merged preferences. */
export async function deleteCustomWallpaper(): Promise<UserPreferencesPayload> {
  const { data } = await mailApi.delete('/users/me/wallpaper');
  return data;
}

export async function fetchFirmWallpaperMeta(): Promise<WallpaperMetaPayload | null> {
  const { data } = await mailApi.get('/wallpapers/firm/meta');
  return (data?.wallpaper as WallpaperMetaPayload | null) ?? null;
}

export interface WallpaperMetaPayload {
  version: string;
  width: number;
  height: number;
  bytes: number;
  updated_at: string;
}

// ── Folders ─────────────────────────────────────────────────────────────────

// Map backend special_use string to frontend MailFolderType
function mapSpecialUseToType(specialUse: string | null): MailFolderType {
  const map: Record<string, MailFolderType> = {
    '\\Inbox': 'inbox',
    '\\Sent': 'sent',
    '\\Drafts': 'drafts',
    '\\Trash': 'deleted',
    '\\Junk': 'junk',
    '\\Archive': 'archive',
    '\\Snoozed': 'snoozed',
    '\\Flagged': 'flagged',
  };
  return (specialUse && map[specialUse]) || 'custom';
}

// Map backend DB row shape to frontend MailFolder interface
function mapFolder(raw: Record<string, unknown>): MailFolder {
  return {
    id: raw.folder_id as string,
    mailboxId: raw.mailbox_id as string,
    name: raw.name as string,
    path: raw.path as string | undefined,
    type: mapSpecialUseToType(raw.special_use as string | null),
    parentId: (raw.parent_folder_id as string) || null,
    unreadCount: (raw.unread_count as number) || 0,
    totalCount: (raw.message_count as number) || 0,
    children: [],
    specialUse: raw.special_use as string | undefined,
    sortOrder: (raw.sort_order as number) || 0,
    iconColor: (raw.icon_color as string) || undefined,
    linkedMatterId: (raw.linked_matter_id as string) || undefined,
    linkedClientId: (raw.linked_client_id as string) || undefined,
    autoFileEnabled: (raw.auto_file_enabled as boolean) || false,
  };
}

/**
 * Build a nested folder tree from the flat list of folders returned by the
 * API. Migrated folders carry their hierarchy in the `path` column
 * (e.g. `INBOX/Clients/Acme`), and `parent_folder_id` is often NULL on
 * migrated rows — so we anchor the tree on `path` rather than `parentId`.
 *
 * Algorithm:
 *   1. Sort by path so parents come before children (`INBOX` before
 *      `INBOX/Clients` before `INBOX/Clients/Acme`).
 *   2. For each folder, find its parent by stripping the last path segment
 *      and matching against folders with that path.
 *   3. Roots (folders whose path has no `/` AND that exist in the input)
 *      are collected for the top level. Children are pushed into their
 *      parent's `children[]`. The leaf `name` is shown for nested folders
 *      so the sidebar reads `Acme` rather than `INBOX/Clients/Acme`.
 *
 * If a folder's stated parent path doesn't exist in the input (orphan), the
 * folder is treated as a root so it's still visible — never silently lost.
 */
function buildFolderTree(folders: MailFolder[], rawByPath: Map<string, string>): MailFolder[] {
  // Sort shortest path first so parents are seen first; preserves explicit sort_order within a depth.
  const sorted = [...folders].sort((a, b) => {
    const pa = rawByPath.get(a.id) ?? a.name;
    const pb = rawByPath.get(b.id) ?? b.name;
    const da = (pa.match(/\//g) ?? []).length;
    const db = (pb.match(/\//g) ?? []).length;
    if (da !== db) return da - db;
    return (a.sortOrder - b.sortOrder) || pa.localeCompare(pb);
  });

  const byPath = new Map<string, MailFolder>();
  const roots: MailFolder[] = [];

  for (const f of sorted) {
    f.children = [];
    const path = rawByPath.get(f.id);
    if (!path || !path.includes('/')) {
      byPath.set(path ?? f.name, f);
      roots.push(f);
      continue;
    }
    // Show the leaf name in the sidebar, keep the full path for create/move calls.
    const segments = path.split('/');
    const leaf = segments[segments.length - 1];
    if (leaf && leaf.length > 0) f.name = leaf;
    const parentPath = segments.slice(0, -1).join('/');
    const parent = byPath.get(parentPath);
    if (parent) {
      parent.children.push(f);
    } else {
      // Orphan (parent missing from response) — surface as root rather than hide.
      roots.push(f);
    }
    byPath.set(path, f);
  }
  return roots;
}

export async function fetchFolders(mailboxId: string, tab?: string): Promise<MailFolder[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/folders`, {
    params: tab ? { tab } : undefined,
  });
  const rawFolders: Record<string, unknown>[] = data.folders ?? data;
  const mapped = rawFolders.map(mapFolder);
  // Build a folder_id → path lookup so the tree-builder can group on `path`.
  const rawByPath = new Map<string, string>();
  for (const r of rawFolders) {
    const id = r.folder_id as string;
    const path = (r.path as string) ?? '';
    if (id) rawByPath.set(id, path);
  }
  return buildFolderTree(mapped, rawByPath);
}

export async function updateFolder(
  mailboxId: string,
  folderId: string,
  updates: { name?: string; icon_color?: string | null; auto_file_enabled?: boolean },
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/folders/${folderId}`, updates);
}

export async function createFolder(
  mailboxId: string,
  name: string,
  options?: {
    parentPath?: string;
    parent_folder_id?: string;
    linked_matter_id?: string;
    linked_client_id?: string;
  },
): Promise<MailFolder> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/folders`, {
    name,
    parentPath: options?.parentPath,
    parent_folder_id: options?.parent_folder_id,
    linked_matter_id: options?.linked_matter_id,
    linked_client_id: options?.linked_client_id,
  });
  return data;
}

export async function deleteFolder(mailboxId: string, folderId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/folders/${folderId}`);
}

export async function markAllAsRead(mailboxId: string, folderId: string): Promise<{ affected: number }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/folders/${folderId}/mark-all-read`);
  return data;
}

export async function emptyFolder(mailboxId: string, folderId: string): Promise<{ affected: number }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/folders/${folderId}/empty`);
  return data;
}

export async function previewFolderRelatedEmails(
  mailboxId: string,
  folderId: string,
): Promise<MailSearchResult & { sourceRuleCount: number }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/folders/${folderId}/fetch-related/preview`);
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return {
    messages: rawMessages.map(mapMessageSummary),
    totalCount: (data.total as number) || rawMessages.length,
    query: `folder-related:${folderId}`,
    sourceRuleCount: (data.source_rule_count as number) || 0,
  };
}

// ── Messages ────────────────────────────────────────────────────────────────

// Map backend EdgeMailMessage entity (snake_case) to frontend MailMessageSummary
function mapMessageSummary(raw: Record<string, unknown>): MailMessageSummary {
  return {
    id: (raw.message_id as string) || (raw.id as string),
    mailboxId: (raw.mailbox_id as string) || (raw.mailboxId as string) || '',
    folderId: (raw.folder_id as string) || (raw.folderId as string) || '',
    from: {
      name: (raw.from_name as string) || (raw.fromName as string) || '',
      email: (raw.from_address as string) || (raw.fromAddress as string) || '',
    },
    subject: (raw.subject as string) || '',
    snippet: (raw.search_snippet as string) || (raw.preview as string) || (raw.snippet as string) || '',
    searchSnippet: (raw.search_snippet as string) || undefined,
    searchAccount: raw.search_account as string | undefined,
    searchFolder: raw.search_folder as string | undefined,
    searchMatches: Array.isArray(raw.search_matches) ? raw.search_matches as MailMessageSummary['searchMatches'] : undefined,
    date: (raw.search_rank !== undefined ? (raw.date_header as string) || (raw.received_at as string) : (raw.received_at as string) || (raw.date_header as string)) || (raw.date as string) || '',
    isRead: !!(raw.is_seen ?? raw.isRead ?? false),
    isFlagged: !!(raw.is_flagged ?? raw.isFlagged ?? false),
    isStarred: !!(raw.is_flagged ?? raw.isStarred ?? false),
    isDraft: !!(raw.is_draft ?? raw.isDraft ?? false),
    hasAttachments: !!(raw.has_attachments ?? raw.hasAttachments ?? false),
    isPrivileged: !!(raw.is_privileged ?? raw.isPrivileged ?? false),
    isOnHold: !!(raw.is_on_hold ?? raw.isOnHold ?? false),
    threadId: (raw.thread_id as string) || (raw.threadId as string) || undefined,
    threadCount: (raw.thread_count as number) || (raw.threadCount as number) || undefined,
    spamScore: raw.spam_score != null ? Number(raw.spam_score) : undefined,
    threadUnreadCount: raw.thread_unread_count != null || raw.threadUnreadCount != null
      ? Number(raw.thread_unread_count ?? raw.threadUnreadCount)
      : undefined,
    threadHasAttachments: raw.thread_has_attachments != null || raw.threadHasAttachments != null
      ? !!(raw.thread_has_attachments ?? raw.threadHasAttachments)
      : undefined,
    threadIsFlagged: raw.thread_is_flagged != null || raw.threadIsFlagged != null
      ? !!(raw.thread_is_flagged ?? raw.threadIsFlagged)
      : undefined,
  };
}

export async function fetchMessages(
  mailboxId: string,
  folderId: string,
  params: {
    tab?: string;
    cursor?: string;
    limit?: number;
    view?: 'flat' | 'threaded';
    filters?: MailListFilter[];
    sender?: string;
    domain?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {},
): Promise<MailMessageListResponse> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/folders/${folderId}/messages`, {
    params: {
      tab: params.tab,
      cursor: params.cursor,
      limit: params.limit ?? 50,
      view: params.view,
      filters: params.filters?.join(',') || undefined,
      sender: params.sender,
      domain: params.domain,
      date_from: params.dateFrom,
      date_to: params.dateTo,
    },
  });
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return {
    messages: rawMessages.map(mapMessageSummary),
    totalCount: (data.total as number) || (data.totalCount as number) || 0,
    nextCursor: data.nextCursor ?? data.next_cursor ?? undefined,
    filterCounts: (data.filterCounts ?? data.filter_counts) as MailListFilterCounts | undefined,
  };
}

// Map backend EdgeMailMessage entity to frontend MailMessage (full detail)
function mapMessageFull(raw: Record<string, unknown>): MailMessage {
  const mapAddr = (a: any) => ({ name: a?.name || '', email: a?.email || a?.address || '' });
  const toAddrs = (raw.to_addresses ?? raw.toAddresses ?? []) as any[];
  const ccAddrs = (raw.cc_addresses ?? raw.ccAddresses ?? []) as any[];
  const rawMatterLinks = ((raw.matter_links ?? raw.matterLinks) as any[] | undefined)?.length
    ? (raw.matter_links ?? raw.matterLinks) as any[]
    : raw.linked_matter_id
      ? [{ id: raw.linked_matter_id as string, matterId: raw.linked_matter_id as string, matterName: '', matterNumber: '' }]
      : [];
  const matterLinks = rawMatterLinks.map((link) => ({
    id: link.id || link.matter_id || link.matterId || '',
    matterId: link.matterId || link.matter_id || link.id || '',
    matterName: link.matterName || link.matter_name || link.description || '',
    matterNumber: link.matterNumber || link.matter_number || '',
    clientId: link.clientId || link.client_id || undefined,
    clientName: link.clientName || link.client_name || undefined,
    clientCode: link.clientCode || link.client_code || undefined,
    clientNumber: link.clientNumber || link.client_number || undefined,
  }));
  const rawClientLinks = ((raw.client_links ?? raw.clientLinks) as any[] | undefined)?.length
    ? (raw.client_links ?? raw.clientLinks) as any[]
    : raw.linked_client_id
      ? [{ id: raw.linked_client_id as string, clientId: raw.linked_client_id as string, clientName: '' }]
      : [];
  const clientLinks = rawClientLinks.map((link) => ({
    id: link.id || link.client_id || link.clientId || '',
    clientId: link.clientId || link.client_id || link.id || '',
    clientName: link.clientName || link.client_name || '',
    clientCode: link.clientCode || link.client_code || undefined,
    clientNumber: link.clientNumber || link.client_number || undefined,
  }));
  if (clientLinks.length === 0) {
    const linkedMatterClient = matterLinks.find((link) => link.clientId);
    if (linkedMatterClient?.clientId) {
      clientLinks.push({
        id: linkedMatterClient.clientId,
        clientId: linkedMatterClient.clientId,
        clientName: linkedMatterClient.clientName || '',
        clientCode: linkedMatterClient.clientCode,
        clientNumber: linkedMatterClient.clientNumber,
      });
    }
  }

  return {
    id: (raw.message_id as string) || (raw.id as string),
    mailboxId: (raw.mailbox_id as string) || (raw.mailboxId as string) || '',
    folderId: (raw.folder_id as string) || (raw.folderId as string) || '',
    from: {
      name: (raw.from_name as string) || '',
      email: (raw.from_address as string) || '',
    },
    to: toAddrs.map(mapAddr),
    cc: ccAddrs.map(mapAddr),
    bcc: ((raw.bcc_addresses ?? raw.bccAddresses ?? []) as any[]).map(mapAddr),
    subject: (raw.subject as string) || '',
    snippet: (raw.preview as string) || (raw.snippet as string) || '',
    bodyHtml: (raw.body_html as string) || (raw.bodyHtml as string) || (raw.body as string) || '',
    bodyText: (raw.body_plain as string) || (raw.bodyText as string) || (raw.body_text as string) || (raw.body as string) || '',
    date: (raw.search_rank !== undefined ? (raw.date_header as string) || (raw.received_at as string) : (raw.received_at as string) || (raw.date_header as string)) || (raw.date as string) || '',
    isRead: !!(raw.is_seen ?? raw.isRead ?? false),
    spamScore: raw.spam_score != null ? Number(raw.spam_score) : undefined,
    isFlagged: !!(raw.is_flagged ?? raw.isFlagged ?? false),
    isStarred: !!(raw.is_flagged ?? raw.isStarred ?? false),
    isDraft: !!(raw.is_draft ?? raw.isDraft ?? false),
    hasAttachments: !!(raw.has_attachments ?? raw.hasAttachments ?? false),
    isPrivileged: !!(raw.is_privileged ?? raw.isPrivileged ?? false),
    isOnHold: !!(raw.is_on_hold ?? raw.isOnHold ?? false),
    attachments: ((raw.attachments ?? []) as any[]).map((a) => {
      const edgeDocs = a.edge_docs || a.edgeDocs || {};
      return {
        id: a.attachment_id || a.id || '',
        filename: a.filename || a.file_name || '',
        mimeType: a.mime_type || a.mimeType || a.content_type || '',
        size: a.size || a.size_bytes || 0,
        contentId: a.content_id || a.contentId || undefined,
        documentId: edgeDocs.status && edgeDocs.status !== 'filed' ? undefined : edgeDocs.document_id || edgeDocs.documentId || a.document_id || a.documentId || undefined,
        documentVersionId: edgeDocs.status && edgeDocs.status !== 'filed' ? undefined : edgeDocs.document_version_id || edgeDocs.documentVersionId || a.document_version_id || a.documentVersionId || undefined,
        edgeDocsStatus: edgeDocs.status || ((a.document_id || a.documentId) && (a.document_version_id || a.documentVersionId) ? 'filed' : 'not_filed'),
        edgeDocsFilingSource: edgeDocs.filing_source || edgeDocs.filingSource || null,
        edgeDocsFiledAt: edgeDocs.filed_at || edgeDocs.filedAt || null,
        edgeDocsErrorCode: edgeDocs.error_code || edgeDocs.errorCode || null,
        edgeDocsErrorMessage: edgeDocs.error_message || edgeDocs.errorMessage || null,
      };
    }),
    labels: ((raw.labels ?? []) as string[]).map((l) => ({ id: l, name: l, color: '' })),
    matterLinks,
    clientLinks,
    headers: {
      'message-id': (raw.message_id_header as string) || '',
      'in-reply-to': (raw.in_reply_to as string) || '',
      references: (raw.references_header as string) || '',
    },
    snoozedUntil: (raw.snoozed_until as string | null) ?? (raw.snoozedUntil as string | null) ?? null,
  };
}

export async function fetchMessage(mailboxId: string, messageId: string): Promise<MailMessage> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/messages/${messageId}`);
  // Backend returns { message: { ...fields, body: "<html>" }, attachments: [...] }
  const raw = data.message ?? data;
  // Merge top-level attachments into the raw message object for mapMessageFull
  if (data.attachments && !raw.attachments) {
    raw.attachments = data.attachments;
  }
  return mapMessageFull(raw);
}

export interface MailMessageLocation {
  messageId: string;
  mailboxId: string;
  folderId: string | null;
}

/**
 * Resolve a receipt-stable message UUID to the mailbox the current user may
 * open. The backend intentionally returns 404 for both missing and
 * unauthorized messages so source receipts cannot enumerate another user's
 * mail.
 */
export async function resolveMessageLocation(messageId: string): Promise<MailMessageLocation> {
  const { data } = await mailApi.get(`/messages/${encodeURIComponent(messageId)}/location`);
  return {
    messageId: String(data.message_id),
    mailboxId: String(data.mailbox_id),
    folderId: data.folder_id ? String(data.folder_id) : null,
  };
}

/**
 * Download the raw RFC 822 source as a .eml file. The backend streams the
 * exact bytes from Dovecot, preserving headers and signatures — which is
 * what "Save as .eml" needs to be legally meaningful. We fetch as a blob
 * (so the bearer token header is honored) and trigger a click on a
 * temporary <a> to drive the browser's download UI; this avoids the
 * `window.open` path which can't attach auth headers.
 */
/**
 * Fetch the raw RFC 822 source of a message as text, for the "View source"
 * viewer. Same authenticated endpoint the .eml download uses.
 */
export async function fetchMessageRawSource(
  mailboxId: string,
  messageId: string,
): Promise<string> {
  const response = await mailApi.get(
    `/mailboxes/${mailboxId}/messages/${messageId}/raw`,
    { responseType: 'text', timeout: 60000, transformResponse: [(data) => data] },
  );
  return typeof response.data === 'string' ? response.data : String(response.data);
}

export async function downloadMessageEml(
  mailboxId: string,
  messageId: string,
  suggestedFilename?: string,
): Promise<void> {
  const response = await mailApi.get(
    `/mailboxes/${mailboxId}/messages/${messageId}/raw`,
    { responseType: 'blob', timeout: 60000 },
  );

  // Prefer the filename the server suggested via Content-Disposition; it
  // already includes the subject and a filesystem-safe encoding. Fall back
  // to the caller-supplied suggestion, then to a generic name.
  const disposition = response.headers?.['content-disposition'] as string | undefined;
  const filename =
    parseContentDispositionFilename(disposition) ||
    suggestedFilename ||
    `${messageId}.eml`;

  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: 'message/rfc822' });

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Delay revoke so Safari can still start the download from the URL
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export interface MailboxBackupExportOptions {
  includeEml?: boolean;
  folderId?: string | null;
}

export interface MailboxBackupProgress {
  loaded: number;
  total: number | null;
  percent: number | null;
  elapsedMs: number;
  bytesPerSecond: number | null;
  estimatedRemainingMs: number | null;
}

export interface MailboxBackupArchive {
  blob: Blob;
  filename: string;
  messageCount: number | null;
  fallbackCount: number | null;
}

export interface MailboxBackupArchiveRequest extends MailboxBackupExportOptions {
  signal?: AbortSignal;
  onProgress?: (progress: MailboxBackupProgress) => void;
}

export async function requestMailboxBackupArchive(
  mailboxId: string,
  options: MailboxBackupArchiveRequest = {},
): Promise<MailboxBackupArchive> {
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const response = await mailApi.post(
    `/mailboxes/${mailboxId}/exports`,
    {
      include_eml: Boolean(options.includeEml),
      folder_id: options.folderId ?? null,
    },
    {
      responseType: 'blob',
      timeout: 300000,
      signal: options.signal,
      onDownloadProgress: (event: AxiosProgressEvent) => {
        const total = typeof event.total === 'number' && Number.isFinite(event.total) ? event.total : null;
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const elapsedMs = Math.max(1, now - startedAt);
        const bytesPerSecond = event.loaded > 0 ? event.loaded / (elapsedMs / 1000) : null;
        const estimatedRemainingMs =
          total && total > 0 && bytesPerSecond && bytesPerSecond > 0
            ? Math.max(0, ((total - event.loaded) / bytesPerSecond) * 1000)
            : null;
        options.onProgress?.({
          loaded: event.loaded,
          total,
          percent: total && total > 0 ? Math.min(100, Math.round((event.loaded / total) * 100)) : null,
          elapsedMs,
          bytesPerSecond,
          estimatedRemainingMs,
        });
      },
    },
  );

  const disposition = response.headers?.['content-disposition'] as string | undefined;
  const filename =
    parseContentDispositionFilename(disposition) ||
    `EdgeMail_Backup_${mailboxId}_${new Date().toISOString().slice(0, 10)}.edgebackup.zip`;

  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: 'application/zip' });

  return {
    blob,
    filename,
    messageCount: parseNullableInt(response.headers?.['x-edgemail-export-message-count']),
    fallbackCount: parseNullableInt(response.headers?.['x-edgemail-export-fallback-count']),
  };
}

export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export async function exportFolderPdf(mailboxId: string, folderId: string): Promise<{ filename: string; messageCount: number | null }> {
  const response = await mailApi.post(
    `/mailboxes/${mailboxId}/folders/${folderId}/export-pdf`,
    {},
    { responseType: 'blob', timeout: 300000 },
  );
  const disposition = response.headers?.['content-disposition'] as string | undefined;
  const filename =
    parseContentDispositionFilename(disposition) ||
    `EdgeMail_Folder_${folderId}_${new Date().toISOString().slice(0, 10)}.pdf`;
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: 'application/pdf' });
  downloadBlob(filename, blob);
  return {
    filename,
    messageCount: parseNullableInt(response.headers?.['x-edgemail-export-message-count']),
  };
}

export interface MailImportResult {
  imported: number;
  skippedDuplicates: number;
  failed: number;
  errors: Array<{ index: number; message: string }>;
}

export async function importMailFile(
  mailboxId: string,
  file: File,
  options?: { folderId?: string | null },
): Promise<MailImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.folderId) formData.append('folder_id', options.folderId);
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/import`, formData, {
    timeout: 300000,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function exportPrivilegeLog(
  mailboxId: string,
  options?: { folderId?: string | null },
): Promise<{ filename: string; rowCount: number | null }> {
  const response = await mailApi.get(`/mailboxes/${mailboxId}/privilege-log`, {
    params: options?.folderId ? { folderId: options.folderId } : undefined,
    responseType: 'blob',
    timeout: 120000,
  });
  const disposition = response.headers?.['content-disposition'] as string | undefined;
  const filename =
    parseContentDispositionFilename(disposition) ||
    `EdgeMail_Privilege_Log_${new Date().toISOString().slice(0, 10)}.csv`;
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: 'text/csv;charset=utf-8' });
  downloadBlob(filename, blob);
  return {
    filename,
    rowCount: parseNullableInt(response.headers?.['x-edgemail-privilege-log-row-count']),
  };
}

function parseContentDispositionFilename(header?: string): string | null {
  if (!header) return null;
  // Prefer the RFC 5987 UTF-8 form; fall back to plain filename.
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const asciiMatch = header.match(/filename="?([^";]+)"?/i);
  return asciiMatch ? asciiMatch[1] : null;
}

function parseNullableInt(value: unknown): number | null {
  const raw = Array.isArray(value) ? value[0] : value;
  const parsed = Number.parseInt(String(raw ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export interface SendQueueStatus {
  queue_id: string;
  status: 'pending' | 'pending_moderation' | 'sending' | 'sent' | 'delivered' | 'deferred' | 'bounced' | 'failed' | 'cancelled' | string;
  last_error: string | null;
  sent_at: string | null;
  bounced_at: string | null;
  bounce_type: 'hard' | 'soft' | null;
  dsn_status: string | null;
}

/** The server's own word on a queued send — what the delivery toast falls back to when no socket event arrives. */
export async function cancelSend(mailboxId: string, queueId: string): Promise<{ cancelled: boolean; reason?: string }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/send/${queueId}/cancel`);
  return data as { cancelled: boolean; reason?: string };
}

export interface ForwardingPolicy {
  external_forwarding: 'disabled' | 'internal_only' | 'allowed';
  max_send_delay_seconds: number;
  firm_domains: string[];
  forward_to: string | null;
  forward_keep_copy: boolean;
}

export async function fetchForwardingPolicy(mailboxId: string): Promise<ForwardingPolicy> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/forwarding-policy`);
  return data as ForwardingPolicy;
}

export interface OutOfOfficeOptions { scope: 'everyone' | 'internal_only'; exclude: string[]; intervalDays: number }
export interface OutOfOfficeSettings {
  enabled: boolean; subject: string; body: string; start: string | null; end: string | null; options: OutOfOfficeOptions;
  installed: boolean; policy: { out_of_office_scope: 'everyone' | 'internal_only' };
}

export async function fetchOutOfOffice(mailboxId: string): Promise<OutOfOfficeSettings> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/out-of-office`);
  return data as OutOfOfficeSettings;
}

export async function updateOutOfOffice(mailboxId: string, input: Partial<Omit<OutOfOfficeSettings, 'installed' | 'policy'>>): Promise<OutOfOfficeSettings> {
  const { data } = await mailApi.put(`/mailboxes/${mailboxId}/out-of-office`, input);
  return data as OutOfOfficeSettings;
}

export type JunkLevel = 'off' | 'standard' | 'aggressive';
export interface JunkListEntry { id: string; address: string | null; domain: string | null; reason?: string }
export interface JunkSettings { level: JunkLevel; blocked: JunkListEntry[]; allowed: JunkListEntry[] }

export async function fetchJunkSettings(mailboxId: string): Promise<JunkSettings> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/junk`);
  return data as JunkSettings;
}
export async function updateJunkLevel(mailboxId: string, level: JunkLevel): Promise<{ level: JunkLevel; installed: boolean }> {
  const { data } = await mailApi.put(`/mailboxes/${mailboxId}/junk`, { level });
  return data as { level: JunkLevel; installed: boolean };
}
export async function addJunkListEntry(mailboxId: string, list: 'blocked' | 'allowed', entry: { address?: string; domain?: string }): Promise<JunkListEntry> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/junk/${list}`, entry);
  return data as JunkListEntry;
}
export async function removeJunkListEntry(mailboxId: string, list: 'blocked' | 'allowed', entryId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/junk/${list}/${entryId}`);
}

export interface DataPolicy {
  retention_days: number | null; aba_ethics_enabled: boolean; soc2_logging_enabled: boolean; gdpr_enabled: boolean;
  on_hold_count: number; privileged_count: number; quota_bytes: number | null;
}
export async function fetchDataPolicy(mailboxId: string): Promise<DataPolicy> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/data-policy`);
  return data as DataPolicy;
}

export async function fetchSendStatus(mailboxId: string, queueId: string): Promise<SendQueueStatus> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/send/${queueId}/status`);
  return data as SendQueueStatus;
}

export async function sendMessage(
  mailboxId: string,
  payload: SendMessagePayload,
): Promise<{ messageId: string; queueId?: string; delivery?: SendDeliverySummary }> {
  // Map camelCase frontend payload to snake_case backend expectation
  const backendPayload = {
    to: payload.to,
    cc: payload.cc,
    bcc: payload.bcc,
    subject: payload.subject,
    body_html: payload.bodyHtml,
    body_plain: payload.bodyText || '',
    signature_already_inserted: payload.signatureAlreadyInserted,
    in_reply_to: payload.inReplyTo,
    attachment_ids: payload.attachmentIds,
    matter_id: payload.matterId,
    client_id: payload.clientId,
    is_privileged: payload.isPrivileged,
    draft_id: payload.draftId,
    send_identity_id: payload.sendIdentityId,
    scheduled_at: payload.scheduledAt,
    link_all: payload.linkAll,
    link_expiry_days: payload.linkExpiryDays,
  };
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/send`, backendPayload);
  return {
    messageId: data.message_id || data.messageId,
    queueId: data.queue_id || data.queueId,
    delivery: data.delivery && typeof data.delivery === 'object' ? (data.delivery as SendDeliverySummary) : undefined,
  };
}

/** Large-message delivery: which attachments went as secure links (server-decided at send). */
export interface SendDeliverySummary {
  mode: 'inline' | 'hybrid';
  linked: Array<{ attachment_id: string; filename: string; size_bytes: number; expires_at: string }>;
  summary: string | null;
}

export interface SentAttachmentLink {
  link_id: string;
  attachment_id: string;
  filename: string;
  size_bytes: number;
  content_type: string | null;
  expires_at: string;
  revoked_at: string | null;
  download_count: number;
  max_downloads: number | null;
  last_download_at: string | null;
  recipient_emails: string[];
  created_at: string;
  state: 'active' | 'expired' | 'revoked';
}

/** Secure links minted for a Sent message, with download receipts. Empty for ordinary messages. */
export async function fetchMessageLinks(mailboxId: string, messageId: string): Promise<SentAttachmentLink[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/messages/${messageId}/links`);
  return Array.isArray(data?.links) ? (data.links as SentAttachmentLink[]) : [];
}

export async function revokeMessageLink(mailboxId: string, messageId: string, linkId: string): Promise<void> {
  await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/links/${linkId}/revoke`);
}

export async function extendMessageLink(mailboxId: string, messageId: string, linkId: string, days = 30): Promise<string> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/links/${linkId}/extend`, { days });
  return String(data?.expires_at ?? '');
}

export async function markAsRead(
  mailboxId: string,
  messageId: string,
  isRead: boolean,
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/read`, { read: isRead });
}

export async function toggleFlag(
  mailboxId: string,
  messageId: string,
  isFlagged: boolean,
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/flag`, { flagged: isFlagged });
}

export async function moveMessage(
  mailboxId: string,
  messageId: string,
  targetFolderId: string,
): Promise<MoveMessageResponse> {
  const { data } = await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/move`, { target_folder_id: targetFolderId });
  return data;
}

export interface SenderAutomationSuggestion {
  automationType: 'sender' | 'conversation';
  automationScope?: 'sender' | 'participant' | 'outbound_recipient';
  senderEmail: string;
  senderName?: string | null;
  counterpartEmail?: string | null;
  counterpartName?: string | null;
  sourceThreadId?: string | null;
  targetFolderId: string;
  targetFolderName: string;
  targetFolderPath: string;
  matchingExistingCount: number;
  autoFileRuleExists: boolean;
}

export interface MoveMessageResponse {
  success: boolean;
  senderAutomation?: SenderAutomationSuggestion | null;
}

export interface SenderAutomationResult {
  success: boolean;
  movedExisting: number;
  autoFileRuleCreated: boolean;
  autoFileRuleExists: boolean;
  suggestion?: SenderAutomationSuggestion | null;
}

export async function organizeSenderMessages(
  mailboxId: string,
  messageId: string,
  targetFolderId: string,
  options: { moveExisting: boolean; createAutoFileRule: boolean },
): Promise<SenderAutomationResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/sender-automation`, {
    target_folder_id: targetFolderId,
    move_existing: options.moveExisting,
    create_auto_file_rule: options.createAutoFileRule,
  });
  return data;
}

export type EmailRuleConditionKind = 'from' | 'to' | 'cc' | 'bcc' | 'participant' | 'recipient' | 'subject' | 'domain';
export type EmailRuleScope = 'existing' | 'future' | 'both';

export interface EmailRuleCandidate {
  id: string;
  kind: EmailRuleConditionKind;
  label: string;
  value: string;
  displayValue: string;
  isOwnedAddress?: boolean;
  warning?: string;
}

export interface EmailRuleDefinition {
  conditionKind: EmailRuleConditionKind;
  conditionValue: string;
  targetFolderId: string;
  scope: EmailRuleScope;
}

export interface EmailRulePreviewMessage {
  id: string;
  from: string | null;
  subject: string | null;
  folderId: string;
  receivedAt: string | null;
}

export interface EmailRulePreviewResult {
  matchCount: number;
  samples: EmailRulePreviewMessage[];
}

export interface EmailRuleApplyResult extends EmailRulePreviewResult {
  success: boolean;
  movedExisting: number;
  ruleCreated: boolean;
}

export interface EmailRuleRerunResult {
  success: boolean;
  ruleId: string;
  movedExisting: number;
  targetFolderId: string;
  targetFolderPath: string;
  remainingOutsideTargetCount: number;
}

export interface ManagedEmailRuleCondition {
  field: string;
  operator: string;
  value: string;
  headerName?: string;
}

export interface ManagedEmailRuleAction {
  type: string;
  folderId?: string;
  folder?: string;
  labels?: string[];
  category?: 'focused' | 'other';
  forwardTo?: string;
  forwardKeepCopy?: boolean;
  rejectMessage?: string;
  /** notify: how the phone treats matching mail (mobile push policy). */
  push?: 'always' | 'never' | 'quiet';
  level?: 'active' | 'time-sensitive';
}

export interface ManagedEmailRule {
  ruleId: string;
  mailboxId: string;
  name: string;
  priority: number;
  conditions: ManagedEmailRuleCondition[];
  conditionMatch: 'all' | 'any';
  actions: ManagedEmailRuleAction[];
  isActive: boolean;
  isAiEnhanced: boolean;
  stopProcessing: boolean;
  source: string;
  createdAt: string | null;
  updatedAt: string | null;
  targetFolderPath: string | null;
  targetFolderId: string | null;
  matchCount: number | null;
  lastMatchAt: string | null;
  existingOutsideTargetCount: number | null;
}

export interface EmailRuleManagementSuggestion {
  id: string;
  type: 'duplicate' | 'stale' | 'backlog' | 'ai_enhanced';
  severity: 'info' | 'warning';
  title: string;
  description: string;
  ruleIds: string[];
}

export interface EmailRuleManagementResult {
  rules: ManagedEmailRule[];
  suggestions: EmailRuleManagementSuggestion[];
}

export async function fetchRuleDraft(
  mailboxId: string,
  messageId: string,
): Promise<{ candidates: EmailRuleCandidate[] }> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/messages/${messageId}/rule-draft`);
  return data;
}

export async function listEmailRules(mailboxId: string): Promise<EmailRuleManagementResult> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/rules`);
  return data;
}

export async function updateEmailRule(
  mailboxId: string,
  ruleId: string,
  patch: { name?: string; priority?: number; isActive?: boolean },
): Promise<ManagedEmailRule> {
  const { data } = await mailApi.patch(`/mailboxes/${mailboxId}/rules/${ruleId}`, {
    name: patch.name,
    priority: patch.priority,
    is_active: patch.isActive,
  });
  return data.rule;
}

export interface EmailRuleFullInput {
  name: string;
  priority?: number;
  isActive?: boolean;
  stopProcessing?: boolean;
  conditionMatch: 'all' | 'any';
  conditions: Array<{ field: string; operator: string; value: string }>;
  actions: ManagedEmailRuleAction[];
}

export async function createEmailRuleFull(
  mailboxId: string,
  input: EmailRuleFullInput,
): Promise<ManagedEmailRule> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/rules/full`, input);
  return data.rule;
}

export async function updateEmailRuleFull(
  mailboxId: string,
  ruleId: string,
  input: EmailRuleFullInput,
): Promise<ManagedEmailRule> {
  const { data } = await mailApi.put(`/mailboxes/${mailboxId}/rules/${ruleId}/full`, input);
  return data.rule;
}

export async function deleteEmailRule(mailboxId: string, ruleId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/rules/${ruleId}`);
}

export async function rerunEmailRule(mailboxId: string, ruleId: string): Promise<EmailRuleRerunResult> {
  const { data } = await mailApi.post<EmailRuleRerunResult>(`/mailboxes/${mailboxId}/rules/${ruleId}/rerun`);
  return data;
}

export async function previewEmailRule(
  mailboxId: string,
  rule: EmailRuleDefinition,
): Promise<EmailRulePreviewResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/rules/preview`, {
    condition_kind: rule.conditionKind,
    condition_value: rule.conditionValue,
    target_folder_id: rule.targetFolderId,
    scope: rule.scope,
  });
  return data;
}

export async function applyEmailRule(
  mailboxId: string,
  rule: EmailRuleDefinition,
): Promise<EmailRuleApplyResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/rules`, {
    condition_kind: rule.conditionKind,
    condition_value: rule.conditionValue,
    target_folder_id: rule.targetFolderId,
    scope: rule.scope,
  });
  return data;
}

export async function reclassifyMessage(
  mailboxId: string,
  messageId: string,
  category: 'focused' | 'other',
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/category`, { category });
}

export async function deleteMessage(mailboxId: string, messageId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/messages/${messageId}`);
}

export async function bulkMoveMessages(
  mailboxId: string,
  messageIds: string[],
  targetFolderId: string,
): Promise<{ moved: number; failed: number; total: number }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/messages/bulk-move`, {
    message_ids: messageIds,
    target_folder_id: targetFolderId,
  });
  return data;
}

export interface FolderMatterLinkRepairPreview {
  folderId: string;
  folderPath: string;
  linkedMatterId: string;
  totalMessages: number;
  alreadyLinkedCount: number;
  unlinkedCount: number;
  conflictingMatterCount: number;
  eligibleCount: number;
  samples: Array<{
    messageId: string;
    subject: string | null;
    from: string | null;
    folderPath: string | null;
    receivedAt: string | null;
    linkedMatterId: string | null;
  }>;
}

export interface FolderMatterLinkRepairResult extends FolderMatterLinkRepairPreview {
  linkedCount: number;
  skippedConflictCount: number;
}

export async function previewFolderMatterLinkRepair(
  mailboxId: string,
  folderId: string,
): Promise<FolderMatterLinkRepairPreview> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/folders/${folderId}/link-messages-to-matter/preview`);
  return data;
}

export async function applyFolderMatterLinkRepair(
  mailboxId: string,
  folderId: string,
  options?: { overrideConflicts?: boolean },
): Promise<FolderMatterLinkRepairResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/folders/${folderId}/link-messages-to-matter/apply`, {
    override_conflicts: options?.overrideConflicts === true,
  });
  return data;
}

export async function linkMessageToMatter(
  mailboxId: string,
  messageId: string,
  matterId: string,
): Promise<{ linkedMatterId: string; movedToFolderId?: string | null; movedToFolderPath?: string | null }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/link-matter`, { matter_id: matterId });
  return {
    linkedMatterId: data.linked_matter_id,
    movedToFolderId: data.moved_to_folder_id,
    movedToFolderPath: data.moved_to_folder_path,
  };
}

export async function linkMessageToClient(
  mailboxId: string,
  messageId: string,
  clientId: string,
): Promise<void> {
  await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/link-client`, { client_id: clientId });
}

// ── Threads ─────────────────────────────────────────────────────────────────

export async function fetchThreadMessages(
  mailboxId: string,
  threadId: string,
): Promise<MailMessageSummary[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/threads/${threadId}/messages`);
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return rawMessages.map(mapMessageSummary);
}

// ── Flagged Messages ────────────────────────────────────────────────────────

export async function fetchFlaggedMessages(
  mailboxId: string,
  options?: { page?: number; limit?: number },
): Promise<{ messages: MailMessageSummary[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.page) params.set('page', String(options.page));
  if (options?.limit) params.set('limit', String(options.limit));
  const qs = params.toString();
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/flagged${qs ? `?${qs}` : ''}`);
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return { messages: rawMessages.map(mapMessageSummary), total: data.total ?? 0 };
}

// ── Drafts ──────────────────────────────────────────────────────────────────

export async function saveDraft(
  mailboxId: string,
  draft: {
    to?: { name: string; email: string }[];
    cc?: { name: string; email: string }[];
    bcc?: { name: string; email: string }[];
    subject?: string;
    body_html?: string;
    body_plain?: string;
    in_reply_to?: string;
    matter_id?: string;
    client_id?: string;
    is_privileged?: boolean;
  },
): Promise<{ draftId: string }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/drafts`, draft);
  return data;
}

export async function updateDraft(
  mailboxId: string,
  draftId: string,
  draft: {
    to?: { name: string; email: string }[];
    cc?: { name: string; email: string }[];
    bcc?: { name: string; email: string }[];
    subject?: string;
    body_html?: string;
    body_plain?: string;
    in_reply_to?: string;
    matter_id?: string;
    client_id?: string;
    is_privileged?: boolean;
  },
): Promise<{ draftId: string }> {
  const { data } = await mailApi.put(`/mailboxes/${mailboxId}/drafts/${draftId}`, draft);
  return data;
}

export async function deleteDraft(
  mailboxId: string,
  draftId: string,
): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/drafts/${draftId}`);
}

// ── Privilege ────────────────────────────────────────────────────────────────

export async function togglePrivilege(
  mailboxId: string,
  messageId: string,
  isPrivileged: boolean,
  privilegeType: 'attorney-client' | 'work-product' = 'attorney-client',
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/privilege`, {
    is_privileged: isPrivileged,
    privilege_type: isPrivileged ? privilegeType : null,
  });
}

export async function setMessageHold(
  mailboxId: string,
  messageId: string,
  payload: { isOnHold: boolean; holdId?: string | null; reason?: string | null },
): Promise<{ isOnHold: boolean; holdId: string | null }> {
  const { data } = await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/hold`, {
    is_on_hold: payload.isOnHold,
    hold_id: payload.holdId,
    reason: payload.reason,
  });
  return {
    isOnHold: Boolean(data.is_on_hold ?? data.isOnHold),
    holdId: data.hold_id ?? data.holdId ?? null,
  };
}

export async function sendMessageToEdgeDocs(
  mailboxId: string,
  messageId: string,
): Promise<{
  documentId: string;
  versionId: string;
  title: string;
  clientId?: string | null;
  matterId?: string | null;
  duplicate?: { isDuplicate?: boolean };
}> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/edgedocs`);
  return data;
}

// ── Labels ──────────────────────────────────────────────────────────────────

export async function fetchLabels(mailboxId: string): Promise<MailLabel[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/labels`);
  return data;
}

export async function createLabel(
  mailboxId: string,
  name: string,
  color: string,
): Promise<MailLabel> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/labels`, { name, color });
  return data;
}

export async function updateLabel(
  mailboxId: string,
  labelId: string,
  updates: Partial<{ name: string; color: string }>,
): Promise<MailLabel> {
  const { data } = await mailApi.patch(`/mailboxes/${mailboxId}/labels/${labelId}`, updates);
  return data;
}

export async function deleteLabel(mailboxId: string, labelId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/labels/${labelId}`);
}

export async function applyLabel(
  mailboxId: string,
  messageId: string,
  labelId: string,
): Promise<void> {
  await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/labels/${labelId}`);
}

export async function removeLabel(
  mailboxId: string,
  messageId: string,
  labelId: string,
): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/messages/${messageId}/labels/${labelId}`);
}

// ── Search ──────────────────────────────────────────────────────────────────

export async function searchMessages(
  mailboxId: string,
  query: string,
  options?: SearchOptions,
): Promise<MailSearchResult> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/search`, { params: searchParams(query, options), signal: options?.signal });
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return {
    messages: rawMessages.map(mapMessageSummary),
    totalCount: (data.total as number) || (data.totalCount as number) || 0,
    query: (data.query as string) || query,
    page: data.page, totalPages: data.totalPages, limit: data.limit, search: data.search,
  };
}

export interface SearchOptions { excludeSpecialUse?: string[]; folderId?: string; page?: number; sort?: 'relevance' | 'newest'; related?: boolean; signal?: AbortSignal }
const searchParams = (query: string, options?: SearchOptions) => ({
  q: query, client_search: '1', related: options?.related ? '1' : undefined, page: options?.page || 1, limit: 30, sort: options?.sort || 'relevance',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  ...(options?.folderId ? { folder_id: options.folderId } : {}),
  ...(options?.excludeSpecialUse?.length ? { exclude_special_use: options.excludeSpecialUse.join(',') } : {}),
});

export async function searchCrossMailbox(query: string, options?: SearchOptions): Promise<MailSearchResult> {
  const { data } = await mailApi.get('/search', { params: searchParams(query, options), signal: options?.signal });
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return {
    messages: rawMessages.map(mapMessageSummary),
    totalCount: (data.total as number) || (data.totalCount as number) || 0,
    query: (data.query as string) || query,
    page: data.page, totalPages: data.totalPages, limit: data.limit, search: data.search,
  };
}

export interface MailSearchInterpretation { status: 'ready' | 'needs_clarification' | 'unavailable'; query?: string; sort?: 'relevance' | 'newest'; message?: string; source: 'deterministic' | 'classifier' }
export async function interpretMailSearch(query: string): Promise<MailSearchInterpretation> {
  const { data } = await mailApi.get('/search', { params: { client_search: '1', action: 'interpret', q: query, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }, timeout: 25000 });
  return data;
}

export async function searchLinkedMessages(
  mailboxId: string,
  filters: {
    linked_matter_id?: string;
    linked_client_id?: string;
    exclude_special_use?: string[];
  },
): Promise<MailSearchResult> {
  const { exclude_special_use, ...rest } = filters;
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/search`, {
    params: {
      q: '',
      ...rest,
      ...(exclude_special_use?.length ? { exclude_special_use: exclude_special_use.join(',') } : {}),
      limit: 500,
    },
  });
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return {
    messages: rawMessages.map(mapMessageSummary),
    totalCount: (data.total as number) || (data.totalCount as number) || 0,
    query: '',
  };
}

// ── Attachments ─────────────────────────────────────────────────────────────

export interface AttachmentRecord {
  attachment_id: string;
  filename: string;
  content_type?: string;
  size_bytes?: number;
  content_id?: string;
  is_inline?: boolean;
}

export interface AttachmentPreviewResponse {
  status: 'ready' | 'processing' | 'unsupported' | 'failed';
  previewType?: 'pdf' | 'image' | 'text';
  url?: string;
  contentType?: string;
  filename?: string;
  message?: string;
}

export async function createAttachmentUploadUrl(
  mailboxId: string,
  payload: {
    draftId: string;
    filename: string;
    contentType: string;
    sizeBytes: number;
    contentId?: string;
    isInline?: boolean;
  },
): Promise<{ attachment: AttachmentRecord; upload_url: string; expires_at: string }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/attachments/upload-url`, {
    draft_id: payload.draftId,
    filename: payload.filename,
    content_type: payload.contentType,
    size_bytes: payload.sizeBytes,
    content_id: payload.contentId,
    is_inline: payload.isInline,
  });
  return data;
}

export async function finalizeAttachmentUpload(
  mailboxId: string,
  attachmentId: string,
): Promise<{ attachment: AttachmentRecord; checksum_sha256: string }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/attachments/finalize`, {
    attachment_id: attachmentId,
  });
  return data;
}

export async function createDmsAttachment(
  mailboxId: string,
  payload: {
    draftId: string;
    documentId: string;
    documentVersionId: string;
  },
): Promise<{ attachment: AttachmentRecord }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/attachments/dms`, {
    draft_id: payload.draftId,
    document_id: payload.documentId,
    document_version_id: payload.documentVersionId,
  });
  return data;
}

export async function cloneAttachmentsToDraft(
  mailboxId: string,
  payload: {
    draftId: string;
    attachmentIds: string[];
    sourceMessageId?: string;
    cloneAttemptId?: string;
  },
): Promise<{ attachments: AttachmentRecord[] }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/attachments/clone`, {
    draft_id: payload.draftId,
    attachment_ids: payload.attachmentIds,
    source_message_id: payload.sourceMessageId,
    clone_attempt_id: payload.cloneAttemptId,
  });
  return data;
}

export async function createForwardDraft(
  mailboxId: string,
  sourceMessageId: string,
  payload: {
    draft?: Record<string, unknown>;
    draftId?: string;
    attachmentIds?: string[];
    cloneAttemptId?: string;
  },
): Promise<{
  draftId: string;
  status: 'ready';
  cloneAttemptId: string;
  attachments: AttachmentRecord[];
}> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/messages/${sourceMessageId}/forward-draft`, {
    draft: payload.draft,
    draft_id: payload.draftId,
    attachment_ids: payload.attachmentIds,
    clone_attempt_id: payload.cloneAttemptId,
  });
  return {
    draftId: data.draftId || data.draft_id,
    status: data.status,
    cloneAttemptId: data.cloneAttemptId || data.clone_attempt_id,
    attachments: data.attachments || [],
  };
}

export async function deleteAttachment(attachmentId: string): Promise<void> {
  await mailApi.delete(`/attachments/${attachmentId}`);
}

export async function getAttachmentDownloadUrl(attachmentId: string): Promise<string> {
  const { data } = await mailApi.get(`/attachments/${attachmentId}/url`);
  return data.url;
}

export interface AttachmentDmsImportResult {
  documentId: string;
  versionId: string;
  title: string;
  clientId?: string | null;
  matterId?: string | null;
  duplicate?: {
    isDuplicate: boolean;
    existingDocumentId?: string;
    existingVersionId?: string;
  };
}

export interface AttachmentDmsSourceToken {
  schema: 'edgelex_edgemail_source_token.v1';
  token: string;
  tokenId: string;
  ingestActionId: string;
  sourceContextSha256: string;
  expiresAt: string;
}

export async function issueAttachmentDmsSourceToken(
  attachmentId: string,
): Promise<AttachmentDmsSourceToken> {
  const { data } = await mailApi.post(`/attachments/${attachmentId}/dms-source-token`);
  return data;
}

export async function importAttachmentToDms(
  attachmentId: string,
  payload: {
    messageId?: string;
    mailboxId?: string;
    clientId?: string;
    matterId?: string;
    title?: string;
    description?: string;
  } = {},
): Promise<AttachmentDmsImportResult> {
  const ingestActionId = crypto.randomUUID();
  const { data } = await mailApi.post(`/attachments/${attachmentId}/dms-import`, {
    message_id: payload.messageId,
    mailbox_id: payload.mailboxId,
    client_id: payload.clientId,
    matter_id: payload.matterId,
    title: payload.title,
    description: payload.description,
  }, {
    timeout: 120000,
    headers: { 'X-EdgeLex-Ingest-Action-Id': ingestActionId },
  });
  return data;
}

export async function downloadAttachmentBlob(attachmentId: string): Promise<Blob> {
  const { data } = await mailApi.get(`/attachments/${attachmentId}/download`, { responseType: 'blob' });
  return data as Blob;
}

export async function requestAttachmentPreview(attachmentId: string): Promise<AttachmentPreviewResponse> {
  const { data } = await mailApi.post(`/attachments/${attachmentId}/preview`);
  return data;
}

export async function fetchAttachmentPreviewFile<T = ArrayBuffer>(
  attachmentId: string,
  responseType: 'arraybuffer' | 'blob' | 'text',
): Promise<T> {
  const { data } = await mailApi.get(`/attachments/${attachmentId}/preview-file`, { responseType });
  return data as T;
}

// ── Recipient Classification ───────────────────────────────────────────────

export interface RecipientClassificationResult {
  internalRecipients: string[];
  externalRecipients: string[];
  allInternal: boolean;
}

export async function classifyRecipientsByRealm(
  mailboxId: string,
  recipients: string[],
): Promise<RecipientClassificationResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/recipients/classify`, { recipients });
  return data;
}

// ── Aliases ─────────────────────────────────────────────────────────────────

export async function fetchAliases(mailboxId: string): Promise<{ id: string; aliasEmail: string }[]> {
  // Backend shape: { aliases: [{ alias_id, source, destination, alias_type, ... }] }
  // Normalize to the simple { id, aliasEmail } contract the UI expects.
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/aliases`);
  const rows: Array<Record<string, unknown>> = Array.isArray(data?.aliases)
    ? data.aliases
    : Array.isArray(data)
      ? data
      : [];
  return rows.map((r) => ({
    id: String(r.alias_id ?? r.id ?? ''),
    aliasEmail: String(r.source ?? r.alias_email ?? r.aliasEmail ?? ''),
  }));
}

export async function createAlias(mailboxId: string, aliasEmail: string): Promise<void> {
  // Backend takes split local_part + domain fields, not a raw email.
  const at = aliasEmail.lastIndexOf('@');
  if (at <= 0 || at === aliasEmail.length - 1) {
    throw new Error('Enter a valid email address');
  }
  const local_part = aliasEmail.slice(0, at);
  const domain = aliasEmail.slice(at + 1);
  await mailApi.post(`/mailboxes/${mailboxId}/aliases`, { local_part, domain });
}

export async function deleteAlias(mailboxId: string, aliasId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/aliases/${aliasId}`);
}

// ── App Passwords ───────────────────────────────────────────────────────────

export async function generateAppPassword(
  mailboxId: string,
  name: string,
  protocols: string[],
): Promise<{ password: string }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/app-passwords`, { name, protocols });
  return data;
}

export async function revokeAppPassword(mailboxId: string, passwordId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/app-passwords/${passwordId}`);
}

// ── Sync ────────────────────────────────────────────────────────────────────

export async function triggerMailboxSync(mailboxId: string): Promise<{ queued: boolean; reason?: string }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/sync`);
  return data;
}

// NOTE: a searchMatters() helper was removed 2026-08-21 — it called
// GET /api/edgemail/matters/search, a route that has never existed on the
// backend. Matter search in the client goes through the shared
// ClientMatterPicker against the DMS APIs.

// ── Report / Snooze ─────────────────────────────────────────────────────────

export type ReportReason = 'spam' | 'phishing' | 'abuse';

export async function reportMessage(
  mailboxId: string,
  messageId: string,
  reason: ReportReason,
  blockSender: boolean = false,
): Promise<{ success: boolean; movedToFolderId: string | null; blocked: boolean }> {
  const { data } = await mailApi.post(
    `/mailboxes/${mailboxId}/messages/${messageId}/report`,
    { reason, block_sender: blockSender },
  );
  return data;
}

export async function snoozeMessage(
  mailboxId: string,
  messageId: string,
  until: Date,
): Promise<{ success: boolean; snoozedFolderId: string | null }> {
  const { data } = await mailApi.post(
    `/mailboxes/${mailboxId}/messages/${messageId}/snooze`,
    { until: until.toISOString() },
  );
  return data;
}

export async function unsnoozeMessage(
  mailboxId: string,
  messageId: string,
): Promise<{ success: boolean; restoredToFolderId: string | null }> {
  const { data } = await mailApi.delete(
    `/mailboxes/${mailboxId}/messages/${messageId}/snooze`,
  );
  return data;
}

// ── App passwords list / signature AI draft ────────────────────────────────

export interface AppPasswordSummary {
  id: string;
  name: string;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
}

export async function listAppPasswords(mailboxId: string): Promise<AppPasswordSummary[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/app-passwords`);
  const rows: Array<Record<string, unknown>> = Array.isArray(data?.app_passwords) ? data.app_passwords : [];
  return rows.map((r) => ({
    id: String(r.app_password_id),
    name: String(r.name ?? ''),
    lastUsedAt: (r.last_used_at as string) ?? null,
    lastUsedIp: (r.last_used_ip as string) ?? null,
    expiresAt: (r.expires_at as string) ?? null,
    isActive: !!r.is_active,
    createdAt: String(r.created_at ?? ''),
  }));
}

export interface SignatureDraft {
  label: string;
  html: string;
  plain: string;
}

export interface SignatureDraftResult {
  variants: SignatureDraft[];
  providerUsed: 'local' | 'frontier';
}

export interface MailboxSignature {
  html: string;
  plain: string;
  position: 'top' | 'bottom' | null;
}

export async function fetchMailboxSignature(mailboxId: string): Promise<MailboxSignature> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}`);
  const mb = data?.mailbox ?? data ?? {};
  return {
    html: (mb.signature_html as string) ?? '',
    plain: (mb.signature_plain as string) ?? '',
    position: (mb.signature_position as 'top' | 'bottom' | null) ?? null,
  };
}

export async function saveMailboxSignature(
  mailboxId: string,
  sig: Partial<MailboxSignature>,
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (sig.html !== undefined) payload.signature_html = sig.html;
  if (sig.plain !== undefined) payload.signature_plain = sig.plain;
  if (sig.position !== undefined) payload.signature_position = sig.position;
  await mailApi.patch(`/mailboxes/${mailboxId}/settings`, payload);
}

export async function saveSendIdentitySignature(
  mailboxId: string,
  identityId: string,
  sig: Partial<MailboxSignature> & { onReplies?: boolean },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (sig.html !== undefined) payload.signature_html = sig.html;
  if (sig.plain !== undefined) payload.signature_plain = sig.plain;
  if (sig.position !== undefined) payload.signature_position = sig.position;
  if (sig.onReplies !== undefined) payload.signature_on_replies = sig.onReplies;
  await mailApi.patch(`/mailboxes/${mailboxId}/send-identities/${identityId}/signature`, payload);
}

export async function draftSignatureWithAI(
  mailboxId: string,
  input: {
    title?: string;
    firm?: string;
    phone?: string;
    notes?: string;
    variantCount?: number;
    preferred?: 'local' | 'frontier';
  },
): Promise<SignatureDraftResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/signature/ai-draft`, input);
  return data;
}

// ── Contact Picker (Phase 2) ────────────────────────────────────────────────
import type {
  ContactDirectoryEntry,
  ContactSearchResult,
  ContactSearchScope,
} from '../types/contact.types';

export type {
  ContactDirectoryEntry,
  ContactSearchResult,
  ContactSearchScope,
} from '../types/contact.types';

export interface SearchContactsArgs {
  q: string;
  scope?: ContactSearchScope;
  mailboxId?: string;
  matterId?: string;
  limit?: number;
}

export async function searchContacts(args: SearchContactsArgs): Promise<ContactSearchResult> {
  const params: Record<string, string | number | undefined> = {
    q: args.q,
    scope: args.scope,
    mailboxId: args.mailboxId,
    matterId: args.matterId,
    limit: args.limit,
  };
  const { data } = await mailApi.get('/contacts/search', { params });
  return data as ContactSearchResult;
}

export async function fetchRecentRecipients(
  mailboxId: string,
  limit = 25,
): Promise<ContactDirectoryEntry[]> {
  const { data } = await mailApi.get('/contacts/recent', {
    params: { mailboxId, limit },
  });
  return (data?.contacts ?? []) as ContactDirectoryEntry[];
}

export async function fetchMatterParticipants(matterId: string): Promise<ContactDirectoryEntry[]> {
  const { data } = await mailApi.get(`/contacts/by-matter/${matterId}`);
  return (data?.contacts ?? []) as ContactDirectoryEntry[];
}

// ── Recipient Intelligence (Phase 4) ────────────────────────────────────────

export type SuggestionReason = 'same_matter' | 'cooccurred';
export type ConflictSeverity = 'block' | 'flag' | 'note';

export interface RecipientSuggestion {
  contact: ContactDirectoryEntry;
  reason: SuggestionReason;
  reasonText: string;
  score: number;
}

export interface RecipientConflict {
  recipientEmail: string;
  severity: ConflictSeverity;
  reason: string;
  matterIds?: string[];
}

export interface IntelligenceCommonInput {
  mailboxId: string;
  currentMatterId?: string;
  recipients: Array<{ email: string; name?: string }>;
}

export async function fetchRecipientSuggestions(
  input: IntelligenceCommonInput,
): Promise<RecipientSuggestion[]> {
  const { data } = await mailApi.post('/contacts/suggestions', input);
  return (data?.suggestions ?? []) as RecipientSuggestion[];
}

export async function fetchRecipientConflicts(
  input: IntelligenceCommonInput & { isPrivileged?: boolean },
): Promise<RecipientConflict[]> {
  const { data } = await mailApi.post('/contacts/conflicts', input);
  return (data?.conflicts ?? []) as RecipientConflict[];
}

// ── Moderation Queue (Phase 5) ──────────────────────────────────────────────

export interface ModerationQueueRow {
  queueId: string;
  mailboxId: string;
  senderEmail: string;
  subject: string;
  to: Array<{ email: string; name?: string }>;
  cc: Array<{ email: string; name?: string }>;
  bcc: Array<{ email: string; name?: string }>;
  groups: Array<{
    groupId: string;
    groupEmail: string;
    displayName: string;
    moderators: string[];
  }>;
  createdAt: string;
  canModerate: boolean;
  isOwnSend: boolean;
}

export async function fetchModerationQueue(mailboxId: string): Promise<ModerationQueueRow[]> {
  const { data } = await mailApi.get('/moderation/queue', { params: { mailboxId } });
  return (data?.rows ?? []) as ModerationQueueRow[];
}

export async function approveModerationQueue(args: {
  queueId: string;
  mailboxId: string;
}): Promise<void> {
  await mailApi.post(`/moderation/queue/${args.queueId}/approve`, { mailboxId: args.mailboxId });
}

export async function rejectModerationQueue(args: {
  queueId: string;
  mailboxId: string;
  reason?: string;
}): Promise<void> {
  await mailApi.post(`/moderation/queue/${args.queueId}/reject`, {
    mailboxId: args.mailboxId,
    reason: args.reason,
  });
}

export default mailApi;
